import type { CSSProperties } from 'react'
import { memo } from 'react'
import { colors } from 'settings/colors'
import type { TColumn, TColumnSorting } from '../SummaryTable.types'
import { getColumnWidth } from '../utils'
import { SortArrows } from './SortArrows/SortArrows'

const ValuesHeaderRow = ({
  autoSortColumns,
  col,
  columnSorting,
  enoughDataToShowSorting,
  onColumnSorterClick,
  thRowStyle,
}: {
  autoSortColumns?: string[]
  col: TColumn
  columnSorting: TColumnSorting
  enoughDataToShowSorting: boolean
  onColumnSorterClick: (col: TColumn) => void
  thRowStyle?: CSSProperties
}) => {
  const isFirstColumn =
    col.key.includes('pointName') || col.key.includes('blockName') || col.key.includes('device')

  const canSort =
    enoughDataToShowSorting &&
    (col.sorter || (autoSortColumns || []).includes(col.dataIndex) || isFirstColumn)

  return (
    <th
      css={{
        padding: '8px 14px',
        textAlign: 'left',
        position: isFirstColumn ? 'sticky' : 'static',
        left: isFirstColumn ? 0 : 'auto',
        backgroundColor: 'white',
        zIndex: 2,
        minWidth: getColumnWidth(isFirstColumn, col.width),
        maxWidth: getColumnWidth(isFirstColumn, col.width),
        overflow: 'hidden',
        borderBottom: `1px solid ${colors.grey200}`,
        whiteSpace: 'nowrap',
        cursor: canSort ? 'pointer' : 'default',
        ...thRowStyle,
        ...(isFirstColumn
          ? {
              'borderRight': 'none',
              '&::after': {
                content: '""',
                display: 'block',
                position: 'absolute',
                top: 0,
                right: 0,
                width: '1px',
                height: '100%',
                backgroundColor: colors.grey200,
                zIndex: 10,
              },
            }
          : {
              borderRight: 'none',
            }),
      }}
      onClick={() => {
        if (canSort) {
          onColumnSorterClick(col)
        }
      }}
    >
      <div
        css={{
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
          fontWeight: 400,
          position: 'relative',
        }}
      >
        <span
          css={{
            flex: 1,
            maxWidth: `calc(${col.width}px - 50px)`,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            display: 'block',
          }}
        >
          {col.title}
        </span>
        {canSort && (
          <SortArrows
            sortOrder={columnSorting.column === col.key ? columnSorting.order : 0}
            isBeingSorted={columnSorting.column === col.key}
            type="values"
          />
        )}
      </div>
    </th>
  )
}

const MemoizedHeaderRow = memo(ValuesHeaderRow) as typeof ValuesHeaderRow

export { MemoizedHeaderRow as ValuesHeaderRow }
